import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, FormGroup, Label, Badge, Button } from "reactstrap";
import { MaterialReactTable } from "material-react-table";
import { Box } from "@mui/material";
import Swal from "sweetalert2";
import { getFromLocalStorage } from "../../../../hooks/useLocalStorage";
import convertToRupees from "../../../../utilities/convertToRupees";
import { getCnForBp, reGenerateCNforCommision } from "../../../../api";
import GetButtons from "../../../common/getButtons";
import debounce from "lodash/debounce";

const BPCreditNoteListing = () => {
  const [creditNotes, setCreditNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [rowSelection, setRowSelection] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCn, setSelectedCn] = useState(null);
  const [gstSeries, setGstSeries] = useState("");
  const [isGstRegistered, setIsGstRegistered] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Server-side pagination & filter state
  const [columnFilters, setColumnFilters] = useState([]);
  const debouncedSetColumnFilters = debounce(setColumnFilters, 500);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  useEffect(() => {
    const user = getFromLocalStorage("user");
    setIsGstRegistered(Boolean(user?.is_gst_registered));
  }, []);

  useEffect(() => {
    fetchCreditNotes(pagination.pageIndex, pagination.pageSize, globalFilter, columnFilters, sorting);
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, columnFilters, sorting]);

  const fetchCreditNotes = async (page, rowsPerPage, searchText, columnFilters, sorting) => {
    setIsLoading(true);
    try {
      const roleId = getFromLocalStorage("ROLE")?.role_id;
      const res = await getCnForBp(roleId, page, rowsPerPage, searchText, columnFilters, sorting);
      console.log("CN fetch response:", res);
      const list = res?.data?.result?.cnDetails || [];
      const total = res?.data?.result?.totalCount || 0;
      setCreditNotes(list);
      setCount(total);
    } catch (err) {
      console.error("Failed to fetch CNs", err);
      Swal.fire("Error", "Unable to fetch credit notes", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = () => {
    fetchCreditNotes(pagination.pageIndex, pagination.pageSize, "", [], []);
  };

  // Map gateway status to user-friendly refund status label
  const getRefundStatusLabel = (status) => {
    if (!status) return "-";
    const s = String(status).toUpperCase();
    if (s.includes("REFUND_COMPLETED")) return "Refunded";
    if (s.includes("FAIL")) return "Refund Failed";
    if (s.includes("QUEUED") || s.includes("PENDING") || s.includes("PROCESSING") || s.includes("INITIAT")) return "Refund Processing";
    return s.replace(/_/g, " ");
  };

  // Map refund status to Badge color
  const getRefundBadgeColor = (status) => {
    if (!status) return "secondary";
    const s = String(status).toUpperCase();
    if (s.includes("REFUND_COMPLETED")) return "success";
    if (s.includes("FAIL")) return "danger";
    if (s.includes("PENDING")) return "warning";
    if (s.includes("QUEUED") || s.includes("PROCESSING") || s.includes("INITIAT")) return "warning";
    return "secondary";
  };

  // Return first relevant transaction info (amount and status)
  const getTransactionData = (row) => {
    if (!row) return { amount: null, status: null };
    const cdtx = row.commission_doc_transactions?.[0] || null;
    if (cdtx) {
      const amt = cdtx.amount != null ? Number(cdtx.amount) : null;
      const status = cdtx.status ?? null;
      return { amount: isNaN(amt) ? null : amt, status };
    }

    const docTx = (row.document_transactions || []).find((t) => t.TYPE === "CUSTOMER_REFUND_PAYMENT") || null;
    if (!docTx) return { amount: null, status: null };

    const safeParse = (s) => {
      if (!s) return null;
      try {
        const p = JSON.parse(s);
        if (typeof p === "string") {
          try { return JSON.parse(p); } catch (e) { return p; }
        }
        return p;
      } catch (e) { return null; }
    };

    const parsed = safeParse(docTx.body) || safeParse(docTx.txnId) || null;
    let amount = null;
    if (parsed && typeof parsed === "object") {
      amount = parsed.amount || parsed.net_amount_debit || parsed.net_amount || parsed.payment_amount || null;
    }
    if (amount != null) {
      const num = Number(amount);
      amount = isNaN(num) ? null : num;
    }

    const status = docTx.status ?? (parsed && parsed.status) ?? null;
    return { amount, status };
  };

  const handleDownloadCN = async (cn) => {
    try {
      Swal.fire({
        title: 'Generating Document...',
        text: 'Please wait while we retrieve the Credit Note',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      const payload = {
        commission_id: cn.commission_id || cn.cn_id || cn.id,
        filterDocumentId: cn.document_id || cn.doc_id || null,
      };
      
      const response = await reGenerateCNforCommision(payload);
      
      Swal.close();
      
      let s3Url = response?.data?.result?.cn_url || response?.data?.result?.url || response?.data?.result;
      
      if (s3Url && typeof s3Url === 'string' && s3Url.startsWith('http')) {
        window.open(s3Url, "_blank");
      } else {
        // Fallback if the original cn_url exists and API didn't return a clear link
        if (cn.cn_url) {
           window.open(cn.cn_url, "_blank");
        } else {
           Swal.fire("Error", "Could not retrieve the download URL", "error");
        }
      }
    } catch (err) {
      console.error("Download failed:", err);
      Swal.close();
      if (cn.cn_url) {
        window.open(cn.cn_url, "_blank");
      } else {
        Swal.fire("Error", "Failed to download the document", "error");
      }
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "cn_number",
      header: "Credit Note No.",
      Cell: ({ row }) => <span>{row.original.cn_number}</span>,
    },
    {
      accessorKey: "document_code",
      header: "Document Code",
      Cell: ({ row }) => <span>{row.original.document_code ?? "-"}</span>,
    },
    {
      accessorKey: "gst_amount",
      header: "GST",
      Cell: ({ row }) => <span>{convertToRupees(row.original.gst_amount || 0, true)}</span>,
    },
    {
      accessorKey: "total_amount",
      header: "Amount",
      Cell: ({ row }) => <span>{convertToRupees(row.original.total_amount || 0, true)}</span>,
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      // filterVariant: 'date',
      Cell: ({ row }) => <span>{row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : "-"}</span>,
    },
    {
      accessorKey: "refund_amount",
      header: "Refund Amount",
      Cell: ({ row }) => {
        const { amount } = getTransactionData(row.original);
        if (amount == null) return <span>-</span>;
        return <span>{convertToRupees(amount, true)}</span>;
      },
    },
    {
      accessorKey: "refund_status",
      header: "Refund Status",
      filterVariant: "select",
      filterSelectOptions: [
        { text: "Refunded", value: "REFUND_COMPLETED" },
        { text: "Refund Failed", value: "REFUND_FAILED" },
        { text: "Refund Queued", value: "REFUND_QUEUED" },
        { text: "Refund Pending", value: "REFUND_PENDING" },
        { text: "Refund In Progress", value: "REFUND_IN_PROGRESS" },
        { text: "Refund Initiated", value: "REFUND_INITIATED" },
      ],
      Cell: ({ row }) => {
        const { status } = getTransactionData(row.original);
        const label = status ? getRefundStatusLabel(status) : "-";
        const color = status ? getRefundBadgeColor(status) : "secondary";
        return <Badge color={color}>{label}</Badge>;
      },
    },
    {
      accessorKey: "actions",
      header: "Download CN",
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const cn = row.original;
        return (
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              size="sm"
              color="link"
              title="Download"
              onClick={() => handleDownloadCN(cn)}
              style={{ padding: "0.25rem 0.35rem", lineHeight: 1 }}
            >
              <i className="fa fa-download" aria-hidden="true" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const openClaimModal = (cn) => {
    setSelectedCn(cn);
    setGstSeries(generateGstSeriesIfEmpty());
    setTermsAccepted(false);
    setModalOpen(true);
  };

  const generateGstSeriesIfEmpty = () => {
    if (isGstRegistered) return "GSTIN-REGISTERED";
    if (gstSeries) return gstSeries;
    const now = new Date();
    const startYear = String(now.getFullYear()).slice(-2);
    const endYear = String(now.getFullYear() + 1).slice(-2);
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    const s = `CPGC/${startYear}-${endYear}/${randomNum}`;
    setGstSeries(s);
    return s;
  };

  return (
    <>
      <MaterialReactTable
        columns={columns}
        data={creditNotes}
        enableGlobalFilter={false}
        initialState={{ density: "compact" }}
        rowCount={count}
        state={{
          isLoading,
          rowSelection,
          columnFilters,
          globalFilter,
          pagination,
          sorting,
          showProgressBars: isLoading,
        }}
        manualPagination
        manualFiltering
        manualSorting
        enableMultiRowSelection
        getRowId={(row) => String(row.cn_number || row.cn_id)}
        onRowSelectionChange={setRowSelection}
        onColumnFiltersChange={debouncedSetColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        renderTopToolbarCustomActions={() => (
          <Box sx={{ display: "flex", gap: "0.5rem", p: "0.5rem" }}>
            <GetButtons onClick={refresh} type="refresh" />
          </Box>
        )}
      />

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(false)}>Claim Credit Note</ModalHeader>
        <ModalBody>
          {selectedCn && (
            <>
              <table style={{ width: "100%", marginBottom: 12 }}>
                <thead>
                  <tr>
                    <th>CN Number</th>
                    <th>Amount</th>
                    <th>GST</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{selectedCn.cn_number}</td>
                    <td>{convertToRupees(selectedCn.total_amount || 0, true)}</td>
                    <td>{convertToRupees(selectedCn.gst_amount || 0, true)}</td>
                  </tr>
                </tbody>
              </table>

              <FormGroup>
                <Label>GST Series</Label>
                <Input type="text" value={isGstRegistered ? "GSTIN-REGISTERED" : (gstSeries || generateGstSeriesIfEmpty())} readOnly />
                <div style={{ fontSize: 12, color: "#666" }}>Auto-generated. Editable server-side if needed.</div>
              </FormGroup>

              <FormGroup check style={{ marginTop: 12 }}>
                <Input type="checkbox" id="terms" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                <Label for="terms" check> I accept terms &amp; conditions </Label>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default BPCreditNoteListing;